import React, { useState, useEffect } from "react";
import { useUploadExcelMutation } from "../../app/api/console/consoleExcelApiSlice";
import { useGetRunAnnouncementStatusByScanQuery } from "../../app/api/console/notificationsApiSlice";
import { useGetPlatformActivityRunsQuery } from "../../app/api/platform/platformActivityApiSlice";
import { findRunForScan } from "./mergeActivityRunsIntoScans";
import { ENVIRONMENTS } from "../../pages/common/ScansByEnvironment";
import NotificationRecipientsModal from "./NotificationRecipientsModal";
import NotificationEmailPreviewModal from "./NotificationEmailPreviewModal";

const EditableScanDetail = ({
  scan,
  onScanUpdate,
  isLoading,
  environment,
  stagingWebsiteId,
  productionWebsiteId,
}) => {
  const [editedScan, setEditedScan] = useState({
    title: "",
    excel_report_uri: null,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);
  const [excelUploadError, setExcelUploadError] = useState(null);
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [notifyFeedback, setNotifyFeedback] = useState(null);
  // A prepare -> review -> approved-send batch for the single manual flow.
  // Re-keyed on every confirm so a fresh preview session never inherits a
  // previous one's prepared artifact/approval state.
  const [previewBatch, setPreviewBatch] = useState(null);

  const useProd = environment === ENVIRONMENTS.PRODUCTION;
  const showNotifyButton =
    environment === ENVIRONMENTS.STAGING ||
    environment === ENVIRONMENTS.PRODUCTION;
  const scanId = scan?.id || scan?.scan_id || scan?.console_scan_id;
  // Roster must match the env of the run being notified. Staging Agents
  // previously always used productionWebsiteId → Platform 422 foreign_recipients
  // (ZENYT-CONSOLE-BACKEND-PROD-V).
  const recipientWebsiteId = useProd
    ? productionWebsiteId || scan?.website_id || null
    : stagingWebsiteId || scan?.website_id || null;

  const [uploadExcel] = useUploadExcelMutation();
  const {
    data: notifyStatus,
    isError: isNotifyStatusError,
    error: notifyStatusError,
    refetch: refetchNotifyStatus,
  } = useGetRunAnnouncementStatusByScanQuery(
    { scanId, useProd },
    { skip: !showNotifyButton || !scanId }
  );

  // CUS-1353: the picker preselects the run's agent recipients plus its
  // launcher. A production run pushed from staging is a legacy-run-* with
  // neither, so both come from the staging run that lists this scan.
  const needsStagingRun =
    useProd &&
    showNotifyConfirm &&
    !scan?.activity_run_agent_slug &&
    Boolean(stagingWebsiteId && scanId);
  const { currentData: stagingRuns, isFetching: isFetchingStagingRuns } =
    useGetPlatformActivityRunsQuery(
      { websiteId: stagingWebsiteId, useProd: false, pageSize: 100 },
      { skip: !needsStagingRun }
    );
  const stagingRun = needsStagingRun ? findRunForScan(stagingRuns, scanId) : null;

  const notificationSentAt = notifyStatus?.notification_sent_at || null;
  const hasBeenNotified = Boolean(notificationSentAt);

  const formatNotifyTimestamp = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  const handleNotifyClick = () => {
    setNotifyFeedback(null);
    setShowNotifyConfirm(true);
  };

  // Recipient selection is confirmed here; the resulting prepare -> preview
  // -> approved-send lifecycle is entirely owned by
  // NotificationEmailPreviewModal so this component never calls the
  // prepare/send APIs (or holds their artifact state) directly.
  const handleRecipientsConfirm = (recipientEmails) => {
    setShowNotifyConfirm(false);
    if (!recipientEmails || recipientEmails.length === 0) {
      setNotifyFeedback({ type: "warn", text: "No email will be sent." });
      return;
    }
    setNotifyFeedback(null);
    setPreviewBatch({
      key: `${scanId}-${Date.now()}`,
      items: [
        {
          key: scanId,
          scanId,
          useProd,
          firePath: "console_manual",
          label: scan?.title,
          recipientEmails,
          hasBeenNotified,
        },
      ],
    });
  };

  const handlePreviewClose = () => {
    setPreviewBatch(null);
    refetchNotifyStatus();
  };

  // Debug what's being received
  // console.log("EditableScanDetail received scan:", scan, "environment:", environment);

  useEffect(() => {
    if (scan) {
      setEditedScan({
        title: scan.title || "",
        excel_report_uri: scan.excel_report_uri || null,
      });
    }
  }, [scan]);

  const handleInputChange = (e) => {
    setEditedScan({
      ...editedScan,
      [e.target.name]: e.target.value,
    });
  };

  const handleExcelFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[Excel Upload] File selected:', file.name, file.type, file.size);

    // Validate file type
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      setExcelUploadError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    setIsUploadingExcel(true);
    setExcelUploadError(null);

    try {
      const scanId = scan.id || scan.scan_id;
      const useProd = environment === 'production';
      
      console.log('[Excel Upload] Starting upload:', { scanId, useProd, environment });
      
      const result = await uploadExcel({
        file,
        scanId,
        useProd,
      }).unwrap();

      console.log('[Excel Upload] Upload result:', result);

      // Update the editedScan with the new URI
      setEditedScan({
        ...editedScan,
        excel_report_uri: result.uri,
      });
      
      console.log('[Excel Upload] editedScan updated with URI:', result.uri);
    } catch (error) {
      console.error('[Excel Upload] Failed to upload Excel:', error);
      setExcelUploadError(`Failed to upload Excel file: ${error.message || error.data?.detail || 'Unknown error'}`);
    } finally {
      setIsUploadingExcel(false);
    }
  };

  const handleRemoveExcel = () => {
    // Send empty string to signal backend to clear the field
    setEditedScan({
      ...editedScan,
      excel_report_uri: "",
    });
  };

  const handleSubmit = () => {
    console.log('[Scan Update] Submitting with editedScan:', editedScan);
    console.log('[Scan Update] Full payload:', {
      ...scan,
      ...editedScan,
      environment,
    });
    onScanUpdate({
      ...scan,
      ...editedScan,
      environment,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedScan({
      title: scan?.title || "",
      excel_report_uri: scan?.excel_report_uri || null,
    });
    setExcelUploadError(null);
    setIsEditing(false);
  };

  if (!scan) {
    return (
      <div className="bg-white rounded-2xl border border-border-subtle p-8 text-center text-body-sm text-text-tertiary">
        Select an agent to view and edit details
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-neutral-subtle rounded w-1/4 mb-6"></div>
          <div className="h-16 bg-surface-neutral-subtle rounded mb-4"></div>
        </div>
      </div>
    );
  }

  const hasChanges =
    scan &&
    (editedScan.title !== scan.title ||
      editedScan.excel_report_uri !== (scan.excel_report_uri || null));

  return (
    <div className="bg-white rounded-2xl border border-border-subtle p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-body-lg font-semibold text-text-primary">Agent Details</h2>
        <div className="flex gap-2 items-center">
          {showNotifyButton && (
            <button
              type="button"
              onClick={handleNotifyClick}
              disabled={isEditing}
              className={`px-3 py-1.5 text-body-xs font-semibold rounded-xl border transition-colors disabled:opacity-50 ${
                hasBeenNotified
                  ? "text-text-tertiary border-border-default bg-surface-neutral hover:bg-surface-neutral-subtle"
                  : "text-white bg-button-primary border-button-primary hover:bg-button-primary-hover"
              }`}
              title={
                hasBeenNotified
                  ? `Sent ${formatNotifyTimestamp(notificationSentAt)}. Click to resend.`
                  : "Send run-complete email"
              }
            >
              {hasBeenNotified ? "Sent · Resend email" : "Send email"}
            </button>
          )}
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-body-xs font-medium text-text-secondary border border-border-default rounded-xl hover:bg-surface-neutral transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!hasChanges}
                className={`px-3 py-1.5 text-body-xs font-semibold text-white rounded-xl transition-colors ${
                  hasChanges
                    ? "bg-button-primary hover:bg-button-primary-hover"
                    : "bg-surface-neutral-subtle text-text-tertiary cursor-not-allowed"
                }`}
              >
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 text-body-xs font-semibold bg-button-primary text-white rounded-xl hover:bg-button-primary-hover transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {notifyFeedback && (
        <p
          className={`text-body-xs mb-4 ${
            notifyFeedback.type === "ok"
              ? "text-accent-green"
              : notifyFeedback.type === "warn"
              ? "text-text-secondary"
              : "text-severity-high"
          }`}
        >
          {notifyFeedback.text}
        </p>
      )}
      {isNotifyStatusError && showNotifyButton && (
        <p className="text-body-xs mb-4 text-text-tertiary">
          Could not load email status
          {notifyStatusError?.data?.detail
            ? `: ${String(notifyStatusError.data.detail).slice(0, 120)}`
            : ""}
        </p>
      )}

      {isEditing ? (
        <div className="space-y-6">
          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1.5">
              Title
            </label>
            <input
              type="text"
              name="title"
              value={editedScan.title}
              onChange={handleInputChange}
              className="block w-full px-3 py-2.5 text-body-sm border border-border-subtle rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-purple/20 focus:border-accent-purple/40"
            />
          </div>

          <div>
            <label className="block text-body-xs font-medium text-text-secondary mb-1.5">
              Excel Report
            </label>
            {editedScan.excel_report_uri ? (
              <div className="flex items-center justify-between p-3 bg-accent-green/5 border border-accent-green/20 rounded-xl">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-accent-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-body-xs text-accent-green font-medium">Excel report configured</span>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveExcel}
                  className="text-body-xs text-severity-high hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileChange}
                  disabled={isUploadingExcel}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                />
                {isUploadingExcel && (
                  <p className="text-xs text-blue-600 mt-1">Uploading...</p>
                )}
                {excelUploadError && (
                  <p className="text-xs text-red-600 mt-1">{excelUploadError}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Upload an Excel file with an issue_id column to enable "Share by Excel" for this scan.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div>
            <h3 className="text-body-lg font-semibold text-text-primary">{scan.title}</h3>
            {scan.created_at && (
              <p className="text-body-xs text-text-tertiary mt-1">
                Created: {new Date(scan.created_at).toLocaleDateString()}
              </p>
            )}
            {scan.id && (
              <p className="text-caption text-text-tertiary mt-1 font-mono">ID: {scan.id}</p>
            )}
            {scan.excel_report_uri ? (
              <p className="text-body-xs text-accent-green mt-1 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Excel report available
              </p>
            ) : (
              <p className="text-body-xs text-text-tertiary mt-1">No Excel report configured</p>
            )}
          </div>
        </div>
      )}
      <NotificationRecipientsModal
        isOpen={showNotifyConfirm}
        onClose={() => setShowNotifyConfirm(false)}
        onConfirm={handleRecipientsConfirm}
        websiteId={recipientWebsiteId}
        useProd={useProd}
        agentSlug={
          scan?.activity_run_agent_slug || stagingRun?.agent_slug || undefined
        }
        runId={scan?.activity_run_id || undefined}
        stagingLauncherUserId={stagingRun?.triggered_by_user_id || undefined}
        contextPending={needsStagingRun && isFetchingStagingRuns}
        title={hasBeenNotified ? "Resend email" : "Send email"}
        description={
          hasBeenNotified
            ? `A run-complete email was already sent${
                notificationSentAt
                  ? ` on ${formatNotifyTimestamp(notificationSentAt)}`
                  : ""
              }. Review the email before sending it again.`
            : "Choose exactly who should receive this run-complete email."
        }
        confirmText="Review email"
      />
      {previewBatch && (
        <NotificationEmailPreviewModal
          key={previewBatch.key}
          isOpen
          onClose={handlePreviewClose}
          mode="single"
          items={previewBatch.items}
        />
      )}
    </div>
  );
};

export default EditableScanDetail;
